// BUILD-046 increment 1b-ii (Part A): the glue between MyWorkPage.tsx and assigneeFilterModel.ts, kept pure so the page only has to hand over its loaded rows.
//
// Where the "Assigned to" filter lives. It is offered in exactly three places, and nowhere else: Company and Team (the same tab, the oversight list) and Assigned by Me. My Tasks and Shared Tasks
// have no such filter (their rows are the Caller's own, or the group queue), so the page never builds a picker, a chip or a filter state for them.
//
// What it is built from. The people offered are the people on the rows the view has LOADED (assigneeFilterModel.assigneeChoicesOf over the row sets the view holds), never a directory, never a
// server read. Team / Company holds two lists (open and completed, the second only once it was read), Assigned by Me holds the requests it has paged in. The filter then narrows rows the page
// already had, so it can never offer, match or reveal a person, a task or a request the viewer was not already given.
//
// This module only turns that into the three things the page needs: the picker's data (or null when there is nobody to offer and no filter is on), the "other filters that also apply" list for the
// empty state, and the empty state itself. Pure module: no React, no I/O. Runtime imports: assigneeFilterModel.ts, jobFilterModel.ts, customerFilterModel.ts and oversightViewModel.ts (all pure). Erasable TypeScript only.

import { assigneeChoicesOf, assigneeFilterActive, assigneeFilterScopeText, assigneePersonLoaded, describeAssigneeFilterEmpty } from './assigneeFilterModel';
import type { AssigneeChoice, AssigneeFilter, AssigneeFilterEmptyView, AssigneePerson } from './assigneeFilterModel';
import { describeJobFilterChip } from './jobFilterModel';
import type { JobFilter, JobListKind } from './jobFilterModel';
import { describeCustomerFilterChip } from './customerFilterModel';
import type { CustomerFilter } from './customerFilterModel';
import { DEPARTMENTS } from './oversightViewModel';

/** The views that may have an Assigned to filter. Company and Team are one view (`team-work`); My Tasks and Shared Tasks are not here on purpose. */
export const ASSIGNEE_FILTER_VIEWS: readonly ['team-work', 'issued'] = Object.freeze(['team-work', 'issued'] as const);

/** What the picker needs from the page (the page adds the id prefix and the change handler). */
export interface AssigneePickerData {
  filter: AssigneeFilter;
  options: AssigneeChoice[];
  scopeText: string;
}

/**
 * The picker's data for a view, from the rows it has loaded (`rowSets`: every list the view holds). Null when no loaded row names a person and no filter is on, so an empty picker is never
 * offered; a filter that is on keeps its picker (so it can be changed or cleared) even after a re-read that no longer lists the person.
 */
export function assigneePickerData<T>(rowSets: readonly (readonly T[])[], peopleOf: (row: T) => readonly AssigneePerson[], filter: AssigneeFilter, kind: JobListKind, subject: string): AssigneePickerData | null {
  const options = assigneeChoicesOf(rowSets, peopleOf);
  if (options.length === 0 && !assigneeFilterActive(filter)) return null;
  const loadedCount = rowSets.reduce((sum, rows) => sum + (Array.isArray(rows) ? rows.length : 0), 0);
  return { filter, options, scopeText: assigneeFilterScopeText(kind, loadedCount, subject) };
}

export interface AlsoApplying {
  /** The status choice that also narrows, already worded ("Status: Open"), or null. */
  status?: string | null;
  /** The Customer / Job filter, when the view has one. */
  job?: JobFilter;
  /** The name the Customer / Job filter goes by in the page's words ("Job" while the Customer filter is also offered); absent, "Customer / Job". */
  jobLabel?: string;
  /** The Customer filter, when the view has one (BUILD-046 Customer association). */
  customer?: CustomerFilter;
  /** The department value that also narrows ('' or absent for all departments). It is written with its label ("Department: Sales"). */
  department?: string;
  /** True when the search box also narrows. */
  searching?: boolean;
}

/** The other things that also narrow the list, in the order the empty state names them: status, Customer / Job, Customer, department, search. Empty when none. */
export function assigneeAlsoApplying(input: AlsoApplying): string[] {
  const also: string[] = [];
  if (typeof input.status === 'string' && input.status.trim() !== '') also.push(input.status.trim());
  const job = input.job === undefined ? null : describeJobFilterChip(input.job, input.jobLabel);
  if (job !== null) also.push(job.text);
  const customer = input.customer === undefined ? null : describeCustomerFilterChip(input.customer);
  if (customer !== null) also.push(customer.text);
  const department = typeof input.department === 'string' ? input.department : '';
  if (department !== '') also.push(`Department: ${DEPARTMENTS.find((d) => d.value === department)?.label ?? department}`);
  if (input.searching === true) also.push('search');
  return also;
}

export interface AssigneeEmptyContext<T> extends AlsoApplying {
  /** The same row sets the picker was built from, so "the person is not on the loaded rows any more" is judged against what the picker offers. */
  rowSets: readonly (readonly T[])[];
  peopleOf: (row: T) => readonly AssigneePerson[];
  /** False for a list read page by page: only the loaded rows were checked. */
  complete: boolean;
  noun: 'tasks' | 'requests';
}

/** The empty state for a chosen person who leaves no row: it names the filter and the others that also apply, and offers to clear this one. Null without a filter. */
export function assigneeEmptyFor<T>(filter: AssigneeFilter, context: AssigneeEmptyContext<T>): AssigneeFilterEmptyView | null {
  if (!assigneeFilterActive(filter)) return null;
  const personLoaded = assigneePersonLoaded(assigneeChoicesOf(context.rowSets, context.peopleOf), filter);
  return describeAssigneeFilterEmpty(filter, { also: assigneeAlsoApplying(context), complete: context.complete, personLoaded, noun: context.noun });
}
